import { CommonBonus } from 'config/types/bonus'
import { UserBonus } from 'config/types/bonus/userBonus'
import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { ModalColorTheme } from 'theme/modal/colors'
import AlchemyModal, { ModalBody, ModalFooter, ModalHeader, ModalProps } from 'UIKit/AlchemyModal'
import Box from 'UIKit/Box/Box'
import Button from 'UIKit/Button'
import Text from 'UIKit/Text'
import UpcomingBonusSection from './components/UpcomingBonusSection'

const UpComingBonusModal: React.FC<ModalProps<{ bonusInfo: (UserBonus | CommonBonus)[] }>> = ({
  onDismiss,
  data,
  ...props
}) => {
  const { bonusInfo } = data
  const { t } = useTranslation()

  const lastItemIndex = Number(bonusInfo?.length) - 1
  const displayBonuses = useMemo(() => {
    bonusInfo[lastItemIndex].info.image = '/images/bonus/last-bonus-upcoming.png'

    return bonusInfo
  }, [bonusInfo])

  return (
    <AlchemyModal minWidth="368px !important" colorTheme={ModalColorTheme.Darkblue} {...props}>
      <ModalHeader colorTheme={ModalColorTheme.Darkblue} />
      <ModalBody>
        <Box mb="24px" maxWidth="320px" mx="auto">
          <Text
            fontSize="20px"
            fontWeight={700}
            lineHeight="normal"
            letterSpacing="-0.24px"
            textAlign="center"
            mb="8px"
          >
            <Trans>Complete your current bonus to get more bonuses</Trans>
          </Text>
          <Text
            color="textTertiary"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
            letterSpacing="-0.28px"
            textAlign="center"
          >
            <Trans>
              After completing a bonus, the system will send the next one to your Bonus Center automatically.
            </Trans>
          </Text>
        </Box>
        <ContentContainer>
          {displayBonuses.map((bonus, index) => {
            const isLastItem = index === displayBonuses.length - 1
            const isCurrentBonus = index === 0
            return (
              <UpcomingBonusSection
                bonus={bonus}
                isLastItem={isLastItem}
                isCurrentBonus={isCurrentBonus}
                key={`upcoming-bonus-${index}`}
              />
            )
          })}
        </ContentContainer>
      </ModalBody>
      <ModalFooter>
        <StyledButton
          fontSize={['14px', '', '12px']}
          primary
          width="100%"
          onClick={() => {
            onDismiss()
          }}
        >
          {t('Got It')}
        </StyledButton>
      </ModalFooter>
    </AlchemyModal>
  )
}

export const StyledButton = styled(Button)<{ fontSize?: any; primary?: boolean }>`
  width: 100%;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  height: 44px;
  padding: 15px 10px;
  font-size: ${({ fontSize }) => fontSize || '10px'};
  font-weight: 600;

  white-space: nowrap;
  text-align: center;

  &:hover {
    transform: scale(1.025) !important;
  }

  ${({ primary, theme }) =>
    primary &&
    css`
      background: ${theme.colors.primaryAlt};
      border-color: ${theme.colors.primaryAlt};
    `}

  &:disabled {
    background: rgba(255, 255, 255, 0.08);

    ${({ primary, theme }) =>
      primary &&
      css`
        background: ${theme.colors.primaryDisabled};
      `}
  }
`

const ContentContainer = styled(Box)`
  overflow-x: scroll;
  padding-bottom: 24px;
`

export default UpComingBonusModal
